import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { X, Heart, LogIn } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useIsMobile } from '@/hooks/use-mobile';

export function MobileAuthPrompt() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isMobilePhone = useIsMobile();
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    // Don't show if user is logged in
    if (user) {
      setIsVisible(false);
      return;
    }

    // Don't show on mobile
    if (!isMobilePhone) {
      setIsVisible(false);
      return;
    }

    // Don't show on auth/register pages
    const authPages = ['/auth', '/register', '/login', '/signup'];
    if (authPages.some(page => location.pathname.startsWith(page))) {
      setIsVisible(false);
      return;
    }

    // Check if dismissed in this session
    const dismissed = sessionStorage.getItem('mobile-auth-prompt-dismissed');
    if (dismissed) {
      setIsDismissed(true);
      setIsVisible(false);
      return;
    }

    // Show after 3 seconds of page load
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, [user, isMobilePhone, location.pathname]);

  const handleDismiss = () => {
    setIsVisible(false);
    setIsDismissed(true);
    sessionStorage.setItem('mobile-auth-prompt-dismissed', 'true');
  };

  const handleSignIn = () => {
    handleDismiss();
    navigate('/auth');
  };

  const handleJoinFree = () => {
    handleDismiss();
    navigate('/register?userType=patient_caregiver');
  };

  if (!isVisible || isDismissed) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-primary to-primary-foreground/90 shadow-lg animate-slide-in-down">
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-semibold mb-1">
              Join Pharma Trial Connect
            </p>
            <p className="text-white/90 text-xs">
              Find clinical trials & access free resources
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={handleSignIn}
              className="text-xs px-3 py-1 h-8 min-h-[44px] whitespace-nowrap"
            >
              <LogIn className="h-3 w-3 mr-1" />
              Sign In
            </Button>
            
            <Button
              size="sm"
              onClick={handleJoinFree}
              className="bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-1 h-8 min-h-[44px] whitespace-nowrap"
            >
              <Heart className="h-3 w-3 mr-1" />
              Join Free
            </Button>
            
            <Button
              size="sm"
              variant="ghost"
              onClick={handleDismiss}
              className="text-white hover:bg-white/20 p-1 h-8 w-8 min-h-[44px] min-w-[44px]"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
